import Link from "next/link";
import Image from "next/image";
import { BlogPost } from "@/types/blog";

interface ProductBlogSectionProps {
  posts: BlogPost[];
  eyebrow?: string;
  title?: string;
  description?: string;
}

const formatDate = (date: Date | string) =>
  new Date(date).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const toIso = (date: Date | string) => new Date(date).toISOString();

const ArrowIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 5l7 7-7 7"
    />
  </svg>
);

export default function ProductBlogSection({
  posts,
  eyebrow = "Insights & Guidance",
  title = "Latest From Our Blog",
  description = "Expert-written advice on treatments, nutrition and healthy habits to support your weight loss journey.",
}: ProductBlogSectionProps) {
  if (!posts.length) return null;

  const [featured, ...rest] = posts;
  const secondary = rest.slice(0, 3);

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-[#F8F7FC] via-white to-white py-14 sm:py-20">
      {/* Decorative blur accents */}
      <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-[#7771B8]/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-20 h-80 w-80 rounded-full bg-[#F6FAED] blur-3xl" />

      <div className="relative max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-20 2xl:px-8">
        {/* Header */}
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between mb-10 sm:mb-12">
          <div className="max-w-2xl">
            <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#7771B8] mb-3">
              <span className="h-px w-6 bg-[#7771B8]/50" />
              {eyebrow}
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#2D3340] font-[family-name:var(--font-playfair)]">
              {title}
            </h2>
            {description && (
              <p className="mt-3 text-sm sm:text-base text-gray-600 leading-relaxed">
                {description}
              </p>
            )}
          </div>

          <Link
            href="/blog"
            className="hidden md:inline-flex items-center gap-2 rounded-lg border border-[#7771B8]/30 bg-white px-5 py-2.5 text-sm font-semibold text-[#7771B8] shadow-sm transition-all duration-200 hover:border-[#7771B8] hover:bg-[#7771B8] hover:text-white hover:shadow-md"
          >
            View All Articles
            <ArrowIcon />
          </Link>
        </div>

        {/* Grid: featured + list */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Featured post */}
          <article className="group lg:col-span-7 relative flex overflow-hidden rounded-3xl bg-white border border-gray-200/80 shadow-sm transition-all duration-300 hover:shadow-xl">
            <Link href={`/blog/${featured.slug}`} className="flex w-full">
              <div className="relative w-full aspect-[16/10] sm:aspect-[16/9] lg:aspect-auto lg:min-h-[440px] overflow-hidden">
                {featured.featuredImage ? (
                  <Image
                    src={featured.featuredImage}
                    alt={featured.title}
                    fill
                    sizes="(max-width: 1024px) 100vw, 58vw"
                    className="object-cover transition-transform duration-700 group-hover:scale-105"
                    priority={false}
                  />
                ) : (
                  <div className="h-full w-full bg-gradient-to-br from-[#7771B8] to-[#6B5B9A]" />
                )}

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />


                <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7">
                  <div className="mb-3 flex items-center gap-3 text-xs font-medium text-white/80">
                    <time dateTime={toIso(featured.publishedAt)}>
                      {formatDate(featured.publishedAt)}
                    </time>
                    {featured.readTime && (
                      <>
                        <span className="text-white/40">•</span>
                        <span>{featured.readTime} min read</span>
                      </>
                    )}
                  </div>

                  <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white leading-snug line-clamp-2">
                    {featured.title}
                  </h3>

                  <p className="mt-3 hidden sm:block text-sm text-white/80 leading-relaxed line-clamp-2">
                    {featured.excerpt}
                  </p>

                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-white">
                    Read Article
                    <ArrowIcon className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </span>
                </div>
              </div>
            </Link>
          </article>

          {/* Secondary posts */}
          {secondary.length > 0 && (
            <div className="lg:col-span-5 flex flex-col gap-4 sm:gap-5">
              {secondary.map((post) => (
                <article
                  key={post.id}
                  className="group flex-1 rounded-2xl border border-gray-200/80 bg-white p-3 sm:p-4 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-[#7771B8]/40 hover:shadow-lg"
                >
                  <Link
                    href={`/blog/${post.slug}`}
                    className="flex h-full items-center gap-4"
                  >
                    <div className="relative h-24 w-24 sm:h-28 sm:w-28 flex-shrink-0 overflow-hidden rounded-xl">
                      {post.featuredImage ? (
                        <Image
                          src={post.featuredImage}
                          alt={post.title}
                          fill
                          sizes="112px"
                          className="object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                      ) : (
                        <div className="h-full w-full bg-gradient-to-br from-[#7771B8] to-[#6B5B9A]" />
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="mb-1.5 flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
                        <time dateTime={toIso(post.publishedAt)}>
                          {formatDate(post.publishedAt)}
                        </time>
                        {post.readTime && (
                          <>
                            <span className="text-gray-300">•</span>
                            <span>{post.readTime} min read</span>
                          </>
                        )}
                      </div>

                      <h3 className="text-sm sm:text-base font-bold leading-snug text-[#2D3340] line-clamp-2 transition-colors duration-200 group-hover:text-[#7771B8]">
                        {post.title}
                      </h3>

                      <p className="mt-1.5 hidden sm:block text-xs text-gray-600 leading-relaxed line-clamp-2">
                        {post.excerpt}
                      </p>
                    </div>

                    <ArrowIcon className="hidden sm:block w-5 h-5 flex-shrink-0 text-gray-300 transition-all duration-200 group-hover:translate-x-1 group-hover:text-[#7771B8]" />
                  </Link>
                </article>
              ))}
            </div>
          )}
        </div>

        {/* Mobile CTA */}
        <div className="mt-8 md:hidden">
          <Link
            href="/blog"
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-[#7771B8]/30 bg-white px-5 py-3 text-sm font-semibold text-[#7771B8] shadow-sm transition-colors hover:bg-[#7771B8] hover:text-white"
          >
            View All Articles
            <ArrowIcon />
          </Link>
        </div>
      </div>
    </section>
  );
}
